import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Audi Q5 slide removed on request: also filtered out if still saved in site-config
const isRemovedSlide = (s) => /audi_q5|\bQ5\b/i.test(`${s?.img || ""} ${s?.title || ""}`) && !/RSQ5|SQ5/i.test(s?.title || "");
export const cleanSlides = (arr) => (arr || []).filter((s) => !isRemovedSlide(s));

export const DEFAULT_SLIDES = [
  {
    overline: "Compact SUV Sportivo",
    title: "AUDI RSQ3.\nFURIA COMPATTA.",
    subtitle: "400 CV di pura adrenalina con il 5 cilindri turbo. Il SUV che gioca con le supercar.",
    cta: "Scopri RSQ3",
    href: "/catalogo?q=RSQ3",
    video: `${BACKEND}/api/static/videos/audi_rsq3_black.mp4`,
    poster: "https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=1920",
  },
  {
    overline: "Icona Sportiva",
    title: "PORSCHE 911 TARGA.\nLEGGENDA APERTA.",
    subtitle: "Il roll-bar argento più iconico del design automobilistico. Disponibile per noleggio premium.",
    cta: "Vedi 911 Targa",
    href: "/catalogo?q=911",
    img: "https://images.unsplash.com/photo-1611821064430-0d40291d0f0b?w=1920&q=80",
    kenburns: "from-tl",
  },
  {
    overline: "Breve Termine",
    title: "L'AUTO CHE VUOI,\nQUANDO LA VUOI.",
    subtitle: "Soluzioni rapide e flessibili, senza burocrazia. Prendi l'auto e parti subito.",
    cta: "Scopri il Noleggio",
    href: "/catalogo?listing_type=rent",
    img: "https://static.prod-images.emergentagent.com/jobs/9e7040e3-0412-4f30-bffa-3465ab599eb7/images/731209af8f5fc7dc400b7bb12e59d6e43f332057d400587e0a2bcdcd48e2a1b4.png",
    kenburns: "from-tl",
  },
  {
    overline: "Abbonamenti",
    title: "GUIDA PREMIUM\nDA 99€ L'ANNO.",
    subtitle: "Tre piani: Basic, Medium e Premium. Una vettura sempre disponibile per te.",
    cta: "Vedi Abbonamenti",
    href: "/abbonamenti",
    img: "https://images.unsplash.com/photo-1645596309663-b7b3c795ad85?w=1920",
    kenburns: "from-br",
  },
];

export default function HeroSlider() {
  const [slides, setSlides] = useState(null);
  const [idx, setIdx] = useState(0);
  const [previewing, setPreviewing] = useState(false);

  const { user } = useAuth();

  useEffect(() => {
    const loadSlides = () => {
      api.get("/site-config").then((r) => {
        if (Array.isArray(r.data?.hero) && r.data.hero.length) {
          const cleaned = cleanSlides(r.data.hero);
          if (cleaned.length) setSlides(cleaned);
          setIdx(0);
          if (!cleaned.length) setSlides(DEFAULT_SLIDES);
        } else {
          setSlides((s) => s || DEFAULT_SLIDES);
        }
      }).catch(() => setSlides((s) => s || DEFAULT_SLIDES));
    };
    loadSlides();
    // Live preview from HeroInlineEditor: show the slide being edited, pause autoplay
    const onPreview = (e) => {
      const { slides: draft, active } = e.detail || {};
      if (!Array.isArray(draft) || !draft.length) return;
      setPreviewing(true);
      setSlides(draft);
      setIdx(Math.max(0, Math.min(active || 0, draft.length - 1)));
    };
    const onPreviewEnd = () => { setPreviewing(false); loadSlides(); };
    window.addEventListener("hero-updated", loadSlides);
    window.addEventListener("hero-preview", onPreview);
    window.addEventListener("hero-preview-end", onPreviewEnd);
    return () => {
      window.removeEventListener("hero-updated", loadSlides);
      window.removeEventListener("hero-preview", onPreview);
      window.removeEventListener("hero-preview-end", onPreviewEnd);
    };
  }, []);

  const deleteCurrentSlide = async () => {
    const cur = slides[idx];
    if (!cur || slides.length <= 1) { toast.error("Deve restare almeno una slide"); return; }
    if (!window.confirm(`Eliminare la slide "${(cur.title || "").replace(/\n/g, " ")}"?`)) return;
    const same = (s) => s.title === cur.title && s.img === cur.img && s.video === cur.video;
    const next = slides.filter((s) => !same(s));
    try {
      await api.put("/admin/site-config", { hero: next });
      toast.success("Slide eliminata");
      window.dispatchEvent(new Event("hero-updated"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore eliminazione");
    }
  };

  useEffect(() => {
    if (!slides || slides.length <= 1 || previewing) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 8000);
    return () => clearInterval(t);
  }, [slides?.length, previewing]);

  if (!slides) return <section data-testid="hero-section" className="relative h-[100vh] min-h-[640px] bg-black" />;
  const cur = slides[Math.min(idx, slides.length - 1)] || DEFAULT_SLIDES[0];

  return (
    <section data-testid="hero-section" data-hero-current={idx} data-hero-preview={previewing ? "1" : undefined} className="relative h-[100vh] min-h-[640px] overflow-hidden bg-black">
      {slides.map((s, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-opacity duration-1000 ${i === idx ? "opacity-100" : "opacity-0"}`}
        >
          {s.video ? (
            <video
              data-testid={`hero-video-${i}`}
              src={s.video}
              poster={s.poster}
              autoPlay
              muted
              loop
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div className={`absolute inset-0 ${i === idx ? `kenburns kenburns-${s.kenburns || "zoom"}` : ""}`}>
              <img src={s.img} alt="" className="w-full h-full object-cover" />
            </div>
          )}
          <div className="absolute inset-0 hero-overlay" />
          <div className="absolute inset-0 hero-vignette" />
        </div>
      ))}

      <div className="relative z-10 h-full flex items-end pb-36 md:pb-40 lg:pb-28">
        <div className="max-w-[1400px] mx-auto w-full px-6 lg:px-12">
          <div key={idx} className="reveal max-w-3xl">
            <div className="flex items-center gap-3 mb-5">
              <div className="text-[#22ff66] text-xs uppercase tracking-[0.4em] font-bold">
                {cur.overline}
              </div>
            </div>
            <h1 className="heading-uc text-4xl sm:text-5xl lg:text-7xl text-white whitespace-pre-line mb-6 leading-[0.95]">
              {cur.title}
            </h1>
            <p className="text-base lg:text-lg text-white/70 max-w-xl mb-8 leading-relaxed">
              {cur.subtitle}
            </p>
            <Link
              to={cur.href || "/"}
              data-testid="hero-cta"
              className="inline-flex items-center gap-3 bg-[#22ff66] hover:bg-[#4EFF87] text-black px-8 py-4 uppercase tracking-widest font-semibold text-sm transition btn-red-glow rounded-full"
            >
              {cur.cta} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>

      {user?.is_admin && (
        <button
          type="button"
          data-testid="hero-delete-slide"
          onClick={deleteCurrentSlide}
          className="edit-mode-only absolute top-28 left-6 z-20 bg-red-600 hover:bg-red-700 text-white text-[10px] uppercase tracking-widest font-bold px-3 h-8 items-center gap-1.5 shadow-lg"
        >
          <Trash2 className="w-3 h-3" /> Elimina questa slide
        </button>
      )}

      {/* indicators */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex gap-3">
        {slides.map((_, i) => (
          <button
            key={i}
            data-testid={`hero-dot-${i}`}
            onClick={() => setIdx(i)}
            className={`h-1 transition-all duration-500 ${i === idx ? "w-12 bg-[#22ff66]" : "w-6 bg-white/30"}`}
          />
        ))}
      </div>

    </section>
  );
}
